import React, { useState } from 'react';
import { connect } from 'react-redux';

import { Box } from '@styleseat/ui';
import withStore from '../../../withStore';
import {
  isInInviteIncentiveTest,
  INCENTIVE_COLLAPSE_COUNT_KEY,
  INVITED_CLIENTS_KEY,
  INCENTIVE_MODAL_KEY,
  hasReachedGoal,
} from '../index';
import type { PublicProvider } from '../../../../api/Providers';
import {
  useJanitor,
  useMount,
  useIsMobile,
} from '../../../../hooks';
import stateManager from '../../../../modules/user/stateManager';
import { IncentiveToAdd } from './IncentiveToAdd';
import { IncentiveToAddModal } from './IncentiveToAddModal';

type IncentiveToAddContainerProps = {
  /** The provider object */
  provider: Pick<PublicProvider, 'creation_time' | 'profile_phase_one_complete_date'>,
  /** The number of clients the pro has */
  numClients: number,
  /** The updated count after sending the invite */
  // eslint-disable-next-line react/require-default-props
  updatedInviteCount?: number,
};

const IncentiveToAddContainer: React.FC<IncentiveToAddContainerProps> = ({
  provider,
  numClients,
  updatedInviteCount,
}) => {
  const [showIncentive, setShowIncentive] = useState(false);
  // whether or not we want to load the collapsed view first
  const [initialCollapsed, setInitialCollapsed] = useState(false);
  const [inviteCount, setInviteCount] = useState(0);
  const [showIncentiveModal, setShowIncentiveModal] = useState(false);

  const {
    creation_time: proCreationTime,
    profile_phase_one_complete_date: p1cCompleteDate,
  } = provider;
  const janitor = useJanitor();
  const inMobileLayout = useIsMobile();
  const isProfileComplete = !!p1cCompleteDate;
  const goalMet = hasReachedGoal(updatedInviteCount, isProfileComplete);

  const handleCloseIncentiveModal = () => {
    setShowIncentiveModal(false);
  };

  useMount(() => {
    janitor.addPromise(Promise.all([
      isInInviteIncentiveTest(proCreationTime, numClients),
      stateManager.getActionValues(
        INVITED_CLIENTS_KEY,
        INCENTIVE_COLLAPSE_COUNT_KEY,
        INCENTIVE_MODAL_KEY,
      ),
    ]))
      .then(([isInTest, data]) => {
        const invitedClients = data[INVITED_CLIENTS_KEY] || {};
        const currentInviteCount = Object.keys(invitedClients).length;
        const dismissCount = data[INCENTIVE_COLLAPSE_COUNT_KEY];
        const modalSeen = data[INCENTIVE_MODAL_KEY];
        const showModal = isInTest && !modalSeen && !goalMet;
        setInitialCollapsed(dismissCount > 1);
        setInviteCount(currentInviteCount);
        setShowIncentive(isInTest && !hasReachedGoal(currentInviteCount, isProfileComplete));
        setShowIncentiveModal(showModal);
      });
  });

  if (showIncentive && !goalMet) {
    return (
      <Box
        alignItems={!inMobileLayout ? 'center' : null}
      >
        <IncentiveToAdd
          clientsInvited={updatedInviteCount || inviteCount}
          initialCollapsed={initialCollapsed}
          inMobileLayout={inMobileLayout}
          profileComplete={isProfileComplete}
        />
        {showIncentiveModal && (
          <IncentiveToAddModal
            onClose={handleCloseIncentiveModal}
          />
        )}
      </Box>
    );
  }

  return null;
};

const mapStateToProps = state => ({
  numClients: state.user.numClients,
});

export default withStore(connect(mapStateToProps)(IncentiveToAddContainer));
